<template>
  <div
    class="lr-report-table"
    :class="{ 'lr-report-table--simple': !hasComparisons }"
    :style="themeVars">
    <div class="lr-toolbar">
      <div class="lr-toolbar__group">
        <button type="button" class="lr-btn" @click="expandAll">
          <v-icon size="14">mdi-arrow-expand-vertical</v-icon>
          Buka Semua
        </button>
        <button type="button" class="lr-btn" @click="collapseAll">
          <v-icon size="14">mdi-arrow-collapse-vertical</v-icon>
          Tutup Semua
        </button>
      </div>
    </div>

    <div class="lr-scroll" :class="{ 'lr-scroll--simple': !hasComparisons }">
      <table class="lr-table">
        <colgroup>
          <col class="lr-col-account">
          <col class="lr-col-saldo">
          <col
            v-for="cmp in comparisons"
            :key="`col-${cmp.id}`"
            class="lr-col-saldo">
        </colgroup>
        <thead>
          <tr>
            <th class="lr-th lr-sticky lr-sticky--account lr-col-account">Akun</th>
            <th class="lr-th lr-col-saldo">
              <span class="lr-th__title">Saldo</span>
              <span v-if="saldoPeriodText" class="lr-th__period">{{ saldoPeriodText }}</span>
            </th>
            <th
              v-for="cmp in comparisons"
              :key="cmp.id"
              class="lr-th lr-col-saldo">
              <span class="lr-th__title lr-th__title--compare">
                Saldo
                <button
                  type="button"
                  class="lr-th__remove"
                  @click.stop="$emit('remove-comparison', cmp.id)">
                  <v-icon size="14" color="#6b7280">mdi-close-circle-outline</v-icon>
                </button>
              </span>
              <span class="lr-th__period">{{ cmp.periodText }}</span>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr class="lr-row lr-row--section lr-row--income">
            <td class="lr-td lr-sticky lr-sticky--account lr-sticky--section lr-col-account lr-td--section" colspan="1">
              <span class="lr-section-label">
                <span class="lr-section-dot lr-section-dot--income"></span>
                Pendapatan
              </span>
            </td>
            <td class="lr-td lr-sticky lr-sticky--section lr-col-saldo lr-num lr-num--bold">{{ displayTotals.pendapatan | price }}</td>
            <td
              v-for="cmp in comparisons"
              :key="`pend-${cmp.id}`"
              class="lr-td lr-sticky lr-sticky--section lr-col-saldo lr-num lr-num--bold">
              {{ cmp.totals.pendapatan | price }}
            </td>
          </tr>

          <tr
            v-for="row in incomeRows"
            :key="`income-${row.id}`"
            :class="rowClass(row, 'income')">
            <td
              class="lr-td lr-sticky lr-sticky--account lr-col-account"
              :class="accountCellClass(row)"
              :style="indentStyle(row.depth)"
              @click="toggleRow(row)">
              <span class="lr-node">
                <v-icon
                  v-if="row.kind === 'group'"
                  class="lr-node__chevron"
                  size="16">
                  {{ row.collapsed ? 'mdi-chevron-right' : 'mdi-chevron-down' }}
                </v-icon>
                <span v-else class="lr-node__spacer"></span>
                <span class="lr-node__number">{{ row.item.number }}</span>
                <span class="lr-node__name">{{ row.item.name }}</span>
              </span>
            </td>
            <td class="lr-td lr-col-saldo lr-num" :class="{ 'lr-num--bold': row.kind === 'group' }">
              {{ getSaldo(row.item) | price }}
            </td>
            <td
              v-for="cmp in comparisons"
              :key="`${row.id}-${cmp.id}`"
              class="lr-td lr-col-saldo lr-num"
              style="font-size: 11px;"
              :class="{ 'lr-num--bold': row.kind === 'group' }">
              {{ getCompareSaldo(cmp.id, row.item) | price }}
            </td>
          </tr>

          <tr class="lr-row lr-row--section lr-row--expense">
            <td class="lr-td lr-sticky lr-sticky--account lr-sticky--section lr-col-account lr-td--section" colspan="1">
              <span class="lr-section-label">
                <span class="lr-section-dot lr-section-dot--expense"></span>
                Biaya
              </span>
            </td>
            <td class="lr-td lr-sticky lr-sticky--section lr-col-saldo lr-num lr-num--bold">{{ displayTotals.biaya | price }}</td>
            <td
              v-for="cmp in comparisons"
              :key="`biaya-${cmp.id}`"
              class="lr-td lr-sticky lr-sticky--section lr-col-saldo lr-num lr-num--bold">
              {{ cmp.totals.biaya | price }}
            </td>
          </tr>

          <tr
            v-for="row in expenseRows"
            :key="`expense-${row.id}`"
            :class="rowClass(row, 'expense')">
            <td
              class="lr-td lr-sticky lr-sticky--account lr-col-account"
              :class="accountCellClass(row)"
              :style="indentStyle(row.depth)"
              @click="toggleRow(row)">
              <span class="lr-node">
                <v-icon
                  v-if="row.kind === 'group'"
                  class="lr-node__chevron"
                  size="16">
                  {{ row.collapsed ? 'mdi-chevron-right' : 'mdi-chevron-down' }}
                </v-icon>
                <span v-else class="lr-node__spacer"></span>
                <span class="lr-node__number">{{ row.item.number }}</span>
                <span class="lr-node__name">{{ row.item.name }}</span>
              </span>
            </td>
            <td class="lr-td lr-col-saldo lr-num" :class="{ 'lr-num--bold': row.kind === 'group' }">
              {{ getSaldo(row.item) | price }}
            </td>
            <td
              v-for="cmp in comparisons"
              :key="`${row.id}-${cmp.id}`"
              class="lr-td lr-col-saldo lr-num"
              style="font-size: 11px;"
              :class="{ 'lr-num--bold': row.kind === 'group' }">
              {{ getCompareSaldo(cmp.id, row.item) | price }}
            </td>
          </tr>
        </tbody>

        <tfoot>
          <tr class="lr-row lr-row--footer">
            <td class="lr-td lr-sticky lr-sticky--account lr-col-account lr-td--footer-label">{{ profitLabel }}</td>
            <td class="lr-td lr-col-saldo lr-num lr-num--bold">{{ displayTotals.labaRugi | price }}</td>
            <td
              v-for="cmp in comparisons"
              :key="`laba-${cmp.id}`"
              class="lr-td lr-col-saldo lr-num lr-num--bold"
              style="font-size: 11px;">
              {{ cmp.totals.labaRugi | price }}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
  import { collectGroupIds, flattenTreeRows } from '@/utils/labaRugiReport'

  const ACCOUNT_WIDTH = 460
  const SALDO_WIDTH = 220
  const SIMPLE_TABLE_MAX_WIDTH = 960

  export default {
    name: 'LabaRugiReportTable',

    props: {
      pendapatan: {
        type: Array,
        default: () => []
      },
      biaya: {
        type: Array,
        default: () => []
      },
      comparisons: {
        type: Array,
        default: () => []
      },
      displayTotals: {
        type: Object,
        required: true
      },
      profitLabel: {
        type: String,
        default: 'Laba Rugi'
      },
      saldoPeriodText: {
        type: String,
        default: ''
      },
      color: {
        type: String,
        default: '#1976D2'
      },
      getSaldo: {
        type: Function,
        required: true
      },
      getCompareSaldo: {
        type: Function,
        default: () => () => 0
      }
    },

    data () {
      return {
        collapsed: []
      }
    },

    computed: {
      hasComparisons () {
        return this.comparisons.length > 0
      },
      themeVars () {
        const saldoCols = 1 + this.comparisons.length

        return {
          '--lr-accent': this.color || '#1976D2',
          '--lr-account-width': `${ACCOUNT_WIDTH}px`,
          '--lr-saldo-width': `${SALDO_WIDTH}px`,
          '--lr-simple-max-width': `${SIMPLE_TABLE_MAX_WIDTH}px`,
          '--lr-head-height': '40px',
          '--lr-table-min-width': this.hasComparisons
            ? `${ACCOUNT_WIDTH + saldoCols * SALDO_WIDTH}px`
            : `${SIMPLE_TABLE_MAX_WIDTH}px`
        }
      },
      incomeRows () {
        return this.buildVisibleRows(this.pendapatan)
      },
      expenseRows () {
        return this.buildVisibleRows(this.biaya)
      }
    },

    watch: {
      pendapatan: {
        immediate: true,
        handler () {
          this.applyDefaultCollapse()
        }
      },
      biaya () {
        this.applyDefaultCollapse()
      }
    },

    methods: {
      applyDefaultCollapse () {
        this.collapsed = []
      },
      buildVisibleRows (items) {
        const rows = flattenTreeRows(items, this.collapsed)
        let stripe = 0

        return rows.map(row => {
          if (row.kind !== 'detail') {
            stripe = 0
            return row
          }

          stripe += 1
          return { ...row, stripe: stripe % 2 === 0 }
        })
      },
      isCollapsed (id) {
        return this.collapsed.indexOf(id) !== -1
      },
      toggleRow (row) {
        if (row.kind !== 'group') return

        const index = this.collapsed.indexOf(row.id)

        if (index === -1) this.collapsed.push(row.id)
        else this.collapsed.splice(index, 1)
      },
      expandAll () {
        this.collapsed = []
      },
      collapseAll () {
        this.collapsed = [
          ...new Set([
            ...collectGroupIds(this.pendapatan),
            ...collectGroupIds(this.biaya)
          ])
        ]
      },
      indentStyle (depth) {
        return {
          paddingLeft: `${12 + (depth * 18)}px`,
          fontSize: '11px'
        }
      },
      accountCellClass (row) {
        return {
          'lr-td--group': row.kind === 'group',
          'lr-td--detail': row.kind === 'detail'
        }
      },
      rowClass (row, section) {
        return [
          'lr-row',
          `lr-row--${section}`,
          row.kind === 'group' ? 'lr-row--group' : 'lr-row--detail',
          row.stripe ? 'lr-row--stripe' : null
        ]
      }
    }
  }
</script>

<style scoped>
  .lr-report-table {
    --lr-bg: #ffffff;
    --lr-bg-muted: #f7f8fa;
    --lr-bg-head: #f3f4f6;
    --lr-bg-section-income: #e8f2f8;
    --lr-bg-section-expense: #fdf4e3;
    --lr-bg-stripe: #fafbfc;
    --lr-bg-footer: #f7f8fa;
    --lr-border: #e0e3e8;
    --lr-text: #1f2933;
    --lr-text-muted: #6b7280;
    --lr-head-text: #374151;
    --lr-sticky-bg: #ffffff;
    --lr-hover: #eef2f7;

    color: var(--lr-text);
    background: var(--lr-bg);
    width: 100%;
  }

  .lr-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
  }

  .lr-toolbar__group {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .lr-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 5px 10px;
    border: 1px solid var(--lr-border);
    border-radius: 6px;
    background: var(--lr-bg);
    color: var(--lr-text);
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;
    transition: background 0.12s ease, border-color 0.12s ease;
  }

  .lr-btn:hover {
    background: var(--lr-bg-muted);
    border-color: #c6ccd4;
  }

  .lr-scroll {
    overflow: auto;
    max-height: calc(100vh - 300px);
    border: 1px solid var(--lr-border);
    border-radius: 8px;
    background: var(--lr-bg);
    -webkit-overflow-scrolling: touch;
    width: 100%;
  }

  .lr-report-table--simple {
    max-width: var(--lr-simple-max-width, 960px);
    width: 100%;
  }

  .lr-scroll--simple {
    overflow-x: hidden;
    max-width: var(--lr-simple-max-width, 960px);
    width: 100%;
  }

  .lr-table {
    border-collapse: separate !important;
    border-spacing: 0 !important;
    table-layout: fixed;
    width: 100%;
    min-width: var(--lr-table-min-width);
    font-size: 13px;
    line-height: 1.4;
  }

  .lr-report-table:not(.lr-report-table--simple) .lr-table {
    width: 100%;
    min-width: var(--lr-table-min-width);
  }

  .lr-report-table--simple .lr-table {
    width: 100%;
    max-width: var(--lr-simple-max-width, 960px);
    min-width: 0;
    table-layout: fixed;
  }

  .lr-report-table--simple col.lr-col-account {
    width: auto;
  }

  .lr-report-table--simple col.lr-col-saldo {
    width: var(--lr-saldo-width);
  }

  .lr-report-table--simple .lr-th.lr-sticky--account {
    position: sticky;
    top: 0;
    left: 0;
    z-index: 5;
    width: auto;
    min-width: 0;
    max-width: none;
    box-shadow: none;
  }

  .lr-report-table--simple .lr-td.lr-sticky--account:not(.lr-sticky--section) {
    position: static;
    left: auto;
    box-shadow: none;
  }

  .lr-report-table--simple .lr-col-account {
    width: auto;
    min-width: 0;
    max-width: none;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
  }

  .lr-report-table--simple .lr-node__name {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    word-break: break-word;
  }

  .lr-report-table--simple .lr-col-saldo {
    width: var(--lr-saldo-width);
    min-width: var(--lr-saldo-width);
  }

  .lr-th,
  .lr-td {
    box-sizing: border-box;
    border-bottom: 1px solid var(--lr-border);
    border-right: 1px solid var(--lr-border);
    padding: 6px 10px;
    vertical-align: middle;
  }

  .lr-th:last-child,
  .lr-td:last-child {
    border-right: none;
  }

  .lr-th {
    position: sticky;
    top: 0;
    z-index: 4;
    background: var(--lr-bg-head) !important;
    color: var(--lr-head-text);
    font-weight: 600;
    font-size: 11px;
    text-align: center;
    min-height: 44px;
    height: auto;
    white-space: normal;
    vertical-align: middle;
    /* border-bottom: 1px solid #d1d5db; */
  }

  .lr-th.lr-col-account {
    text-align: left;
  }

  .lr-th.lr-col-saldo {
    min-width: var(--lr-saldo-width);
    width: var(--lr-saldo-width);
  }

  .lr-th.lr-sticky {
    z-index: 6;
  }

  .lr-th__title {
    display: block;
    font-weight: 700;
    line-height: 1.2;
  }

  .lr-th__title--compare {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
  }

  .lr-th__period {
    display: block;
    margin-top: 2px;
    font-size: 10px;
    font-weight: 400;
    line-height: 1.25;
    color: var(--lr-text-muted);
    white-space: normal;
    word-break: break-word;
  }

  .lr-th__remove {
    display: inline-flex;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0;
    opacity: 0.75;
    color: #6b7280;
  }

  .lr-th__remove:hover {
    opacity: 1;
  }

  .lr-sticky {
    position: sticky;
    background: var(--lr-sticky-bg);
  }

  .lr-sticky--account {
    left: 0;
    z-index: 3;
    min-width: var(--lr-account-width);
    width: var(--lr-account-width);
    max-width: var(--lr-account-width);
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.06);
  }

  .lr-report-table:not(.lr-report-table--simple) .lr-sticky--account {
    min-width: var(--lr-account-width);
    width: auto;
    max-width: none;
  }

  .lr-th.lr-sticky--account {
    background: var(--lr-bg-head) !important;
    text-align: left;
    position: sticky;
    top: 0;
    left: 0;
    z-index: 6;
  }

  .lr-report-table:not(.lr-report-table--simple) col.lr-col-account {
    width: auto;
  }

  .lr-report-table:not(.lr-report-table--simple) col.lr-col-saldo {
    width: var(--lr-saldo-width);
  }

  .lr-col-account {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .lr-col-saldo {
    min-width: var(--lr-saldo-width);
    width: var(--lr-saldo-width);
    text-align: right;
  }

  .lr-td.lr-col-saldo {
    overflow: visible;
  }

  .lr-num {
    font-variant-numeric: tabular-nums;
    text-align: right;
    white-space: nowrap;
    font-size: 11px;
  }

  .lr-num--bold {
    font-weight: 700;
  }

  .lr-td--section {
    font-weight: 700;
    font-size: 13px;
  }

  .lr-section-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .lr-section-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .lr-section-dot--income {
    background: #0072b2;
    border-radius: 50%;
  }

  .lr-section-dot--expense {
    background: #e69f00;
    border-radius: 1px;
  }

  .lr-row--section .lr-td {
    position: sticky;
    top: var(--lr-head-height, 52px);
    z-index: 3;
  }

  .lr-row--section.lr-row--income .lr-td {
    background: var(--lr-bg-section-income);
  }

  .lr-row--section.lr-row--expense .lr-td {
    background: var(--lr-bg-section-expense);
    border-top: 1px solid var(--lr-bg);
  }

  .lr-row--section .lr-sticky--account {
    left: 0;
    z-index: 5;
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.06);
  }

  .lr-row--section.lr-row--income .lr-sticky--account,
  .lr-row--section.lr-row--income .lr-sticky--section {
    background: var(--lr-bg-section-income) !important;
  }

  .lr-row--section.lr-row--expense .lr-sticky--account,
  .lr-row--section.lr-row--expense .lr-sticky--section {
    background: var(--lr-bg-section-expense) !important;
  }

  .lr-report-table--simple .lr-row--section .lr-sticky--account {
    position: sticky;
    top: var(--lr-head-height, 52px);
    left: auto;
    box-shadow: none;
  }

  .lr-node {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
  }

  .lr-node__chevron {
    color: var(--lr-text) !important;
    flex: 0 0 auto;
  }

  .lr-node__spacer {
    width: 16px;
    flex-shrink: 0;
  }

  .lr-node__number {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    margin-right: 6px;
    flex-shrink: 0;
  }

  .lr-node__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  .lr-td--group {
    cursor: pointer;
    user-select: none;
  }

  .lr-row--group .lr-node__number,
  .lr-row--group .lr-node__name {
    font-weight: 600;
  }

  .lr-row--detail .lr-td,
  .lr-row--detail .lr-sticky {
    background: var(--lr-sticky-bg);
  }

  .lr-row--stripe .lr-td,
  .lr-row--stripe .lr-sticky {
    background: var(--lr-bg-stripe);
  }

  .lr-row--detail:hover .lr-td,
  .lr-row--detail:hover .lr-sticky {
    background: var(--lr-hover);
  }

  .lr-row--group:hover .lr-td {
    background: var(--lr-bg-muted);
  }

  .lr-row--footer .lr-td {
    background: var(--lr-bg-footer);
    border-top: 2px solid var(--lr-border);
    font-weight: 700;
  }

  .lr-td--footer-label {
    font-size: 14px;
  }

  @media (max-width: 1264px) {
    .lr-table {
      font-size: 12px;
    }

    .lr-scroll {
      max-height: calc(100vh - 380px);
    }
  }
</style>
